import {
  redactSecretEnvValues,
  redactSensitiveString,
  sanitizeUrl,
} from './redaction'
import { incrementTelemetryHealth } from './telemetry-health'
import type { ObservabilityContext } from './types'

/** Names are stable catalog entries; instance identifiers belong in context. */
export const METRIC_TAG_NAMES = [
  'operation',
  'domain',
  'runtime',
  'outcome',
  'reason',
  'provider',
  'dependency',
  'kind',
  'transport',
  'method',
  'status_class',
  'route_template',
  'cache_state',
  'attempt_bucket',
  'trigger',
  'workflow',
  'step',
  'model_family',
  'tool',
  'surface',
  'phase',
  'engine',
  'device_class',
  'navigation_type',
  'connection_state',
  'batch_size_bucket',
  'workload',
  'resource',
  'action',
  'result',
  'fallback',
  'streaming',
  'sample_policy',
  'function',
  'db_operation',
  'collection_family',
  'app',
  'query_name',
  'backend',
  'device_tier',
  'zoom_regime',
  'metric_source',
  'statistic',
  'measure',
  'visibility',
  'hook',
  'retryable',
  'feature',
] as const

export type MetricTagName = (typeof METRIC_TAG_NAMES)[number]
export type MetricTagValue = string | number | boolean
export type MetricTags = Partial<Record<MetricTagName, MetricTagValue>>
export type MetricType = 'count' | 'distribution'
export type MetricUnit =
  | 'count'
  | 'millisecond'
  | 'byte'
  | 'token'
  | 'percent'
  | 'ratio'
  | 'usd'
export type MetricOutcome =
  | 'success'
  | 'failure'
  | 'timeout'
  | 'cancelled'
  | 'rejected'
  | 'partial'
  | 'skipped'
  | 'unknown'
export type OperationOutcome = MetricOutcome
export type MetricErrorOutcome = Extract<
  MetricOutcome,
  'failure' | 'timeout' | 'cancelled' | 'rejected'
>
const errorOutcomes = new WeakMap<object, MetricErrorOutcome>()

/** Preserve an owner's semantic outcome without changing SDK control-flow names. */
export function registerMetricErrorOutcome(
  error: unknown,
  outcome: MetricErrorOutcome
): void {
  if (
    outcome !== 'failure' &&
    outcome !== 'timeout' &&
    outcome !== 'cancelled' &&
    outcome !== 'rejected'
  ) {
    return
  }
  if (
    (typeof error === 'object' && error !== null) ||
    typeof error === 'function'
  ) {
    errorOutcomes.set(error, outcome)
  }
}

/** Local object identity only: serialization and durable boundaries do not carry it. */
export function registeredMetricErrorOutcome(
  error: unknown
): MetricErrorOutcome | undefined {
  return (typeof error === 'object' && error !== null) ||
    typeof error === 'function'
    ? errorOutcomes.get(error)
    : undefined
}

const METRIC_OUTCOMES = new Set<MetricOutcome>([
  'success',
  'failure',
  'timeout',
  'cancelled',
  'rejected',
  'partial',
  'skipped',
  'unknown',
])

export function isMetricOutcome(value: unknown): value is MetricOutcome {
  return (
    typeof value === 'string' && METRIC_OUTCOMES.has(value as MetricOutcome)
  )
}

export type MetricDefinition = {
  readonly name: string
  readonly type: MetricType
  readonly unit: MetricUnit
  /** At most sixteen dimensions per metric; never add raw URLs or IDs. */
  readonly tags: ReadonlyArray<MetricTagName>
  /** Optional closed vocabularies for dimensions with known values. */
  readonly tagValues?: Partial<
    Record<MetricTagName, ReadonlyArray<MetricTagValue>>
  >
}

export type MetricOptions = {
  tags?: MetricTags
  context?: ObservabilityContext
}

export type MetricObservation = {
  readonly schema_version: 1
  readonly metric: {
    readonly name: string
    readonly type: MetricType
    readonly unit: MetricUnit
    readonly value: number
    readonly tags: Readonly<MetricTags>
  }
  readonly context?: Readonly<ObservabilityContext>
}

// Identity, not Object.isFrozen(), proves these graphs were built from validated
// primitives here. A caller's frozen object or proxy must never gain this proof.
const ownedMetricObservations = new WeakSet<MetricObservation>()

export function isOwnedMetricObservation(
  value: unknown
): value is MetricObservation {
  return (
    typeof value === 'object' &&
    value !== null &&
    ownedMetricObservations.has(value as MetricObservation)
  )
}

function ownMetricObservation(
  metric: MetricObservation['metric'],
  context: ObservabilityContext | undefined
): MetricObservation {
  Object.freeze(metric.tags)
  Object.freeze(metric)
  if (context) {
    Object.freeze(context)
  }
  const observation = Object.freeze({
    schema_version: 1 as const,
    metric,
    ...(context ? { context } : {}),
  })
  ownedMetricObservations.add(observation)
  return observation
}

/** Sinks receive immutable records and never await IO. */
export type MetricSink =
  | ((observation: MetricObservation) => void)
  | ((observation: MetricObservation) => boolean)
export type MetricRecorder = (
  definition: MetricDefinition,
  value: number,
  options?: MetricOptions
) => boolean

const TAG_NAMES = new Set<string>(METRIC_TAG_NAMES)
const UNITS = new Set<MetricUnit>([
  'count',
  'millisecond',
  'byte',
  'token',
  'percent',
  'ratio',
  'usd',
])
const METRIC_NAME = /^brew\.[a-z][a-z0-9_.]{1,126}$/
const TAG_VALUE = /^[a-zA-Z0-9_./:[\]()-]{1,128}$/
const UUID_VALUE =
  /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i
/** Preserve the numeric observation even inside Convex's 3.5 KiB envelope. */
export const MAX_METRIC_TAG_BYTES = 1024
export const MAX_METRIC_CONTEXT_BYTES = 768
const PRIORITY_TAGS: ReadonlyArray<MetricTagName> = [
  'operation',
  'domain',
  'runtime',
  'outcome',
]
type CompiledMetricDefinition = {
  readonly name: string
  readonly type: MetricType
  readonly unit: MetricUnit
  readonly orderedTags: ReadonlyArray<MetricTagName>
}
// Only defineMetric's own copied, frozen objects enter this cache. It contains
// contract structure, never caller values, correlation context, or secrets.
const compiledMetricDefinitions = new WeakMap<
  MetricDefinition,
  CompiledMetricDefinition | null
>()

function compileMetricDefinition(
  definition: MetricDefinition
): CompiledMetricDefinition | null {
  // Preserve the external definition's read order and capture each field once.
  const name = definition.name
  const type = definition.type
  const unit = definition.unit
  const declaredTags = definition.tags
  if (
    typeof name !== 'string' ||
    !METRIC_NAME.test(name) ||
    (type !== 'count' && type !== 'distribution') ||
    !UNITS.has(unit) ||
    !Array.isArray(declaredTags)
  ) {
    return null
  }
  const tagCount = declaredTags.length
  if (!Number.isSafeInteger(tagCount) || tagCount < 0 || tagCount > 16) {
    return null
  }
  const definitionTags: Array<MetricTagName> = []
  for (let index = 0; index < tagCount; index += 1) {
    const tag = declaredTags[index]
    if (typeof tag !== 'string' || !TAG_NAMES.has(tag)) {
      return null
    }
    definitionTags.push(tag as MetricTagName)
  }
  return {
    name,
    type,
    unit,
    orderedTags: [
      ...PRIORITY_TAGS.filter((tag) => definitionTags.includes(tag)),
      ...definitionTags.filter((tag) => !PRIORITY_TAGS.includes(tag)),
    ],
  }
}

const HTTP_METHODS = new Set([
  'get',
  'post',
  'put',
  'patch',
  'delete',
  'head',
  'options',
  'connect',
  'trace',
])

/** Requests can supply arbitrary method tokens; metric dimensions cannot. */
export function metricHttpMethod(value: unknown): string {
  if (typeof value !== 'string' || !value) {
    return 'unknown'
  }
  if (value.length > 16) {
    return 'other'
  }
  const method = value.toLowerCase()
  return HTTP_METHODS.has(method) ? method : 'other'
}
const CONTEXT_KEYS = [
  'requestId',
  'operationId',
  'orgId',
  'brandId',
  'userId',
  'chatId',
  'emailId',
  'automationId',
  'executionId',
  'sendId',
  'workflowName',
  'workflowRunId',
  'workflowStepId',
  'route',
  'method',
  'statusCode',
  'workflowAttempt',
] as const satisfies ReadonlyArray<keyof ObservabilityContext>

/** Definitions are immutable so application code cannot change a metric's contract. */
export function defineMetric<const T extends MetricDefinition>(
  definition: T
): T {
  const immutable = Object.freeze({
    ...definition,
    tags: Object.freeze([...definition.tags]),
    ...(definition.tagValues
      ? {
          tagValues: Object.freeze(
            Object.fromEntries(
              Object.entries(definition.tagValues).map(([key, values]) => [
                key,
                Object.freeze([...(values ?? [])]),
              ])
            )
          ),
        }
      : {}),
  }) as T
  const compiled = compileMetricDefinition(immutable)
  if (compiled) {
    Object.freeze(compiled.orderedTags)
    Object.freeze(compiled)
  }
  // Invalid definitions still increment diagnostics when an observation is
  // attempted; merely declaring one must not change health counters.
  compiledMetricDefinitions.set(immutable, compiled)
  return immutable
}

function safeTagValue(value: unknown): value is MetricTagValue {
  if (typeof value === 'boolean') {
    return true
  }
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && Math.abs(value) <= 1000
  }
  return (
    typeof value === 'string' &&
    TAG_VALUE.test(value) &&
    !value.includes('://') &&
    !UUID_VALUE.test(value) &&
    // This alphabet excludes email/assignment separators except ':'. Below
    // eight characters no configured secret or token-shaped run can fit.
    ((value.length < 8 && !value.includes(':')) ||
      redactSensitiveString(value) === value)
  )
}

/** Validated ASCII scalars contain no characters that JSON must escape. */
function scalarJsonLength(value: MetricTagValue): number {
  if (typeof value === 'string') {
    return value.length + 2
  }
  return String(value).length
}

/** Keep durable identifiers searchable without making them metric dimensions. */
export function sanitizeMetricContext(
  context: ObservabilityContext | undefined
): ObservabilityContext | undefined {
  if (!context) {
    return undefined
  }
  const result: Record<string, string | number> = {}
  let bytes = 2
  for (const key of CONTEXT_KEYS) {
    const value = context[key]
    let safe: string | number | undefined
    if (
      typeof value === 'number' &&
      Number.isSafeInteger(value) &&
      value >= 0
    ) {
      if (key === 'statusCode' || key === 'workflowAttempt') {
        safe = value
      }
    } else if (typeof value === 'string' && value.length <= 128) {
      if (key === 'route') {
        safe = sanitizeUrl(value)
      } else if (
        /^[a-zA-Z0-9_.:-]+$/.test(value) &&
        redactSecretEnvValues(value) === value
      ) {
        safe = value
      }
    }
    if (safe !== undefined) {
      // Routes may use the malformed-URL fallback, which can retain Unicode
      // and JSON escapes. All other retained strings passed the ASCII rule.
      const encodedLength =
        key === 'route'
          ? new TextEncoder().encode(JSON.stringify(safe)).byteLength
          : scalarJsonLength(safe)
      const size = key.length + encodedLength + 4
      if (bytes + size <= MAX_METRIC_CONTEXT_BYTES) {
        result[key] = safe
        bytes += size
      } else {
        incrementTelemetryHealth('metric_context_dropped')
      }
    } else if (value !== undefined) {
      incrementTelemetryHealth('metric_context_dropped')
    }
  }
  return Object.keys(result).length > 0 ? result : undefined
}

/** Invalid telemetry is discarded at the boundary, never thrown into business code. */
export function buildMetricObservation(
  definition: MetricDefinition,
  value: number,
  options: MetricOptions = {}
): MetricObservation | null {
  try {
    const cached = compiledMetricDefinitions.get(definition)
    const compiled =
      cached === undefined ? compileMetricDefinition(definition) : cached
    if (!compiled) {
      incrementTelemetryHealth('metric_invalid_definition')
      return null
    }
    const { name, type, unit, orderedTags } = compiled
    if (
      !Number.isFinite(value) ||
      value < 0 ||
      value > 1e15 ||
      (type === 'count' && !Number.isSafeInteger(value)) ||
      (unit === 'percent' && value > 100) ||
      (unit === 'ratio' && value > 1)
    ) {
      incrementTelemetryHealth('metric_invalid_value')
      return null
    }
    const tags: MetricTags = {}
    let tagBytes = 2
    for (const key of orderedTags) {
      const rawValue = options.tags?.[key]
      const tagValue =
        key === 'method' && rawValue !== undefined
          ? metricHttpMethod(rawValue)
          : rawValue
      if (!safeTagValue(tagValue)) {
        if (rawValue !== undefined) {
          incrementTelemetryHealth('metric_tag_dropped')
        }
        continue
      }
      const allowed = definition.tagValues?.[key]
      if (allowed && !allowed.includes(tagValue)) {
        incrementTelemetryHealth('metric_tag_dropped')
        continue
      }
      const size = key.length + scalarJsonLength(tagValue) + 4
      if (tagBytes + size <= MAX_METRIC_TAG_BYTES) {
        tags[key] = tagValue
        tagBytes += size
      } else {
        incrementTelemetryHealth('metric_tag_dropped')
      }
    }
    const context = sanitizeMetricContext(options.context)
    return ownMetricObservation({ name, type, unit, value, tags }, context)
  } catch {
    incrementTelemetryHealth('metric_invalid_metadata')
    return null
  }
}

/** Derive the paired operation duration without rereading caller metadata. */
export function operationDurationObservation(
  completed: MetricObservation,
  durationMs: number
): MetricObservation | null {
  if (
    !isOwnedMetricObservation(completed) ||
    completed.metric.name !== 'brew.operation.completed' ||
    completed.metric.type !== 'count' ||
    completed.metric.unit !== 'count' ||
    !Number.isFinite(durationMs) ||
    durationMs < 0 ||
    durationMs > 1e15
  ) {
    return null
  }
  return ownMetricObservation(
    {
      name: 'brew.operation.duration',
      type: 'distribution',
      unit: 'millisecond',
      value: durationMs,
      tags: completed.metric.tags,
    },
    completed.context
  )
}

export function createMetricRecorder(sink: MetricSink): MetricRecorder {
  return (definition, value, options) => {
    const observation = buildMetricObservation(definition, value, options)
    if (!observation) {
      return false
    }
    try {
      if (sink(observation) === false) {
        incrementTelemetryHealth('metric_sink_failed')
        return false
      }
      incrementTelemetryHealth('metric_accepted')
      return true
    } catch {
      incrementTelemetryHealth('metric_sink_failed')
      return false
    }
  }
}

let runtimeRecorder: MetricRecorder | undefined

/** Install only in runtime bootstrap; adapters may use an explicit recorder instead. */
export function installMetricSink(sink: MetricSink): () => void {
  const previous = runtimeRecorder
  const installed = createMetricRecorder(sink)
  runtimeRecorder = installed
  return () => {
    if (runtimeRecorder === installed) {
      runtimeRecorder = previous
    }
  }
}

/** Shared helpers can record without importing a server-only or browser SDK. */
export const recordMetric: MetricRecorder = (definition, value, options) => {
  if (!runtimeRecorder) {
    incrementTelemetryHealth('metric_sink_missing')
    return false
  }
  return runtimeRecorder(definition, value, options)
}

export function metricNow(): number {
  try {
    return typeof performance !== 'undefined' ? performance.now() : Date.now()
  } catch {
    return Date.now()
  }
}

export function classifyMetricError(error: unknown): MetricErrorOutcome {
  const registered = registeredMetricErrorOutcome(error)
  if (registered) {
    return registered
  }
  try {
    if (error && typeof error === 'object' && 'name' in error) {
      if (error.name === 'AbortError' || error.name === 'ResponseAborted') {
        return 'cancelled'
      }
      if (error.name === 'TimeoutError') {
        return 'timeout'
      }
    }
  } catch {
    // Proxied or hostile thrown values must not replace the original error.
  }
  return 'failure'
}

export const OPERATION_METRIC_TAGS = [
  'operation',
  'domain',
  'runtime',
  'outcome',
  'reason',
  'kind',
  'provider',
  'transport',
  'route_template',
  'method',
  'status_class',
  'surface',
  'phase',
  'cache_state',
  'workflow',
  'step',
] as const satisfies ReadonlyArray<MetricTagName>

export const OPERATION_METRICS = {
  duration: /* @__PURE__ */ defineMetric({
    name: 'brew.operation.duration',
    type: 'distribution',
    unit: 'millisecond',
    tags: OPERATION_METRIC_TAGS,
  }),
  completed: /* @__PURE__ */ defineMetric({
    name: 'brew.operation.completed',
    type: 'count',
    unit: 'count',
    tags: OPERATION_METRIC_TAGS,
  }),
  workload: /* @__PURE__ */ defineMetric({
    name: 'brew.operation.workload',
    type: 'distribution',
    unit: 'count',
    tags: ['operation', 'domain', 'runtime', 'outcome', 'workload'],
  }),
} as const

const OPERATION_NAME_DEFINITION = OPERATION_METRICS.completed

/** Validate a span name live, independently of an overridden metric operation. */
export function metricSpanName(
  name: string,
  definition: MetricDefinition
): string {
  if (definition !== OPERATION_NAME_DEFINITION) {
    // The exported registry can be replaced at runtime. Preserve its legacy
    // contract, including malformed or externally supplied definitions.
    const operation = buildMetricObservation(definition, 1, {
      tags: { operation: name },
    })?.metric.tags.operation
    return typeof operation === 'string' ? operation : 'operation.unknown'
  }
  try {
    if (safeTagValue(name)) {
      return typeof name === 'string' ? name : 'operation.unknown'
    }
    if (name !== undefined) {
      incrementTelemetryHealth('metric_tag_dropped')
    }
  } catch {
    incrementTelemetryHealth('metric_invalid_metadata')
  }
  return 'operation.unknown'
}

export const CRON_SKIP_REASONS = [
  'unauthorized',
  'not_production',
  'no_actor',
  'health_only',
  'revalidate_only',
  'none_due',
  'active_run',
] as const

export const CRON_METRICS = {
  duration: /* @__PURE__ */ defineMetric({
    name: 'brew.cron.tick.duration',
    type: 'distribution',
    unit: 'millisecond',
    tags: [
      'operation',
      'route_template',
      'trigger',
      'outcome',
      'runtime',
      'reason',
    ],
    tagValues: { reason: CRON_SKIP_REASONS },
  }),
  completed: /* @__PURE__ */ defineMetric({
    name: 'brew.cron.tick.completed',
    type: 'count',
    unit: 'count',
    tags: [
      'operation',
      'route_template',
      'trigger',
      'outcome',
      'runtime',
      'reason',
    ],
    tagValues: { reason: CRON_SKIP_REASONS },
  }),
  workload: /* @__PURE__ */ defineMetric({
    name: 'brew.cron.tick.workload',
    type: 'distribution',
    unit: 'count',
    tags: [
      'operation',
      'route_template',
      'trigger',
      'outcome',
      'runtime',
      'step',
      'workload',
    ],
  }),
} as const
